import React, { useState, useEffect } from 'react';
import { DataTable } from 'mantine-datatable';
import { TextInput, Pagination, Card, Button } from '@mantine/core';
import { IconSearch, IconStar, IconHourglass } from '@tabler/icons-react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { useAuth } from '../../../Layouts/context/Authcontext';
import api from '../../../../api';
import * as XLSX from 'xlsx'; // Import XLSX for Excel export
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { IconReload } from '@tabler/icons-react'; // Import a reload icon from a package or your icon set

const animatedComponents = makeAnimated();
const PAGE_SIZE = 10;

const CardCompTableLeakage = ({ onParamsChange }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const { accessToken, client } = useAuth();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [clients, setClients] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedScope, setSelectedScope] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingTable, setLoadingTable] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isSingleDate, setIsSingleDate] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [displayedRecords, setDisplayedRecords] = useState([]);

  const handleDateChange = (update) => {
    // If only one date is selected, set today as the end date
    if (update[0] && !update[1]) {
      setDateRange([update[0], new Date()]);
    } else {
      setDateRange(update);
    }
  };

  // Fetch Clients
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        let baseUrls = getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrls}/client/getAllClient`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.data && Array.isArray(response.data)) {
          const clientOptions = response.data
            .filter((clientItem) => client.includes(clientItem._id))
            .map((clientItem) => ({
              label: clientItem.client,
              value: clientItem._id,
            }));
          setClients(clientOptions);
        }
      } catch (error) {
        // console.error('Error fetching clients:', error);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [accessToken, client]);

  // Fetch Scopes based on selected client
  const fetchScopes = async (clientId) => {
    setLoadingScopes(true);
    try {
      let baseUrls = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrls}/qaDashboard/getClientByScope?clientId=${clientId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.data && Array.isArray(response.data.data)) {
        const scopeOptions = response.data.data.map((scopeItem) => ({
          label: scopeItem.displayName || scopeItem.scope || 'Unknown Scope',
          value: scopeItem._id,
        }));
        setScopes(scopeOptions);
      } else {
        setScopes([]);
      }
    } catch (error) {
      // console.error('Error fetching scopes:', error);
      setScopes([]);
    } finally {
      setLoadingScopes(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const getMTDDates = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      formattedMtdStartDate: formatDate(startOfMonth),
      formattedMtdEndDate: formatDate(today),
    };
  };

  // Fetch initial data (MTD leakage table)
  useEffect(() => {
    const { formattedMtdStartDate, formattedMtdEndDate } = getMTDDates();
    const fetchMtdTable = async () => {
      try {
        let baseUrls = getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrls}/managerDashboard/getCustomDateLeakageTableData`, {
          params: {
            startDate: formattedMtdStartDate,
            endDate: formattedMtdEndDate,
          },
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.data) {
          const data =
            response.data.data && Array.isArray(response.data.data)
              ? response.data.data
              : response.data.records && Array.isArray(response.data.records)
              ? response.data.records
              : [];
          setTableData(data);
        }
      } catch (error) {
        // console.error('Error fetching MTD leakage table data:', error);
      }
    };

    fetchMtdTable();
  }, [accessToken]);

  // // Fetch data when filters change
  // const fetchData = async () => {
  //   const formattedStartDate = formatDate(fromDate);
  //   const formattedEndDate = formatDate(toDate);
  //   const clientId = selectedClient ? selectedClient.value : '';
  //   const scopeId = selectedScope ? selectedScope.value : '';
  //   let tableParams;

  //   if (clientId && scopeId && formattedStartDate && formattedEndDate) {
  //     tableParams = { startDate: formattedStartDate, endDate: formattedEndDate, scopeId, clientId };
  //   } else if (clientId && scopeId) {
  //     tableParams = { scopeId, clientId };
  //   } else if (clientId) {
  //     tableParams = { clientId };
  //   }

  //   if (onParamsChange) {
  //     onParamsChange(tableParams);
  //   }

  //   try {
  //     let baseUrls= getRoundRobinBaseUrl();
  //     const response = await api.get(
  //       `${baseUrls}/managerDashboard/getCustomDateLeakageTableData`,
  //       { params: tableParams, headers: { Authorization: `Bearer ${accessToken}` } }
  //     );

  //     if (response.data) {
  //       const data =
  //         response.data.data && Array.isArray(response.data.data)
  //           ? response.data.data
  //           : response.data.records && Array.isArray(response.data.records)
  //           ? response.data.records
  //           : [];
  //       setTableData(data);
  //     }
  //     setPage(1);
  //   } catch (error) {
  //     // console.error('Error fetching custom leakage table data:', error);
  //   }
  // };
  const fetchData = async () => {
    setLoadingTable(true); // Start loading
    const today = new Date();
    const formattedStartDate = formatDate(fromDate);
    const formattedEndDate = formatDate(toDate || today);
    const clientId = selectedClient ? selectedClient.value : '';
    const scopeId = selectedScope ? selectedScope.value : '';

    let tableParams = {};

    if (clientId) tableParams.clientId = clientId;
    if (scopeId) tableParams.scopeId = scopeId;
    if (formattedStartDate) {
      tableParams.startDate = formattedStartDate;
    }
    if (fromDate && toDate && fromDate.getTime() !== toDate.getTime()) {
      tableParams.endDate = formattedEndDate;
    }

    if (onParamsChange) {
      onParamsChange(tableParams);
    }

    try {
      let baseUrls = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrls}/managerDashboard/getCustomDateLeakageTableData`, {
        params: tableParams,
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.data && Array.isArray(response.data.data)) {
        setTableData(response.data.data);
        setTotalCount(response.data.count || response.data.data.length);
      } else {
        setTableData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching paginated data:', error);
    } finally {
      setLoadingTable(false); // Stop loading
    }
  };

  useEffect(() => {
    fetchData();
    setPage(1);
  }, [accessToken, selectedClient, selectedScope, fromDate, toDate]);

  useEffect(() => {
    let filtered = tableData.filter((item) => Object.values(item).join(' ').toLowerCase().includes(search.toLowerCase()));
    const startIdx = (page - 1) * PAGE_SIZE;
    const endIdx = startIdx + PAGE_SIZE;
    setDisplayedRecords(filtered.slice(startIdx, endIdx));
  }, [tableData, search, page]);

  const handleClearFilters = () => {
    setSelectedClient(null);
    setSelectedScope(null);
    setFromDate(null);
    setToDate(null);
    setScopes([]);
    setPage(1);
  };

  // Filter records based on the search input
  const searchFilteredRecords = Array.isArray(tableData)
    ? tableData.filter((item) => Object.values(item).join(' ').toLowerCase().includes(search.toLowerCase()))
    : [];
  // const displayedRecords = searchFilteredRecords.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Export to Excel using XLSX
  // const exportToExcel = () => {
  //   if (!tableData || tableData.length === 0) {
  //     alert('No data available to export');
  //     return;
  //   }

  //   const formattedData = tableData.map((record) => ({
  //     'Emp ID': record.employeeId || '',
  //     'Emp Name': record.employeeName || '',
  //     Scope: record.scope || '',
  //     Norms: record.norms || '',
  //     'Logged Hrs': record.totalLoggedHours || '',
  //     'Productive Hrs': record.totalProductiveHours || '',
  //     'Leakage Hrs': record.totalLeakageHours || '',
  //     'Effectiveness %': record.toolEffectiveness || '',
  //   }));
  //   const worksheet = XLSX.utils.json_to_sheet(formattedData);
  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, 'LeakageData');
  //   XLSX.writeFile(workbook, 'LeakageData.xlsx');
  // };
  const exportToExcel = async () => {
    setDownloading(true); // show loader
    const formattedStartDate = formatDate(fromDate);
    const formattedEndDate = formatDate(toDate);
    const clientId = selectedClient ? selectedClient.value : '';
    const scopeId = selectedScope ? selectedScope.value : '';

    let tableParams = { page: 1, limit: totalCount || 1000 }; // fetch all

    if (clientId) tableParams.clientId = clientId;
    if (scopeId) tableParams.scopeId = scopeId;
    if (formattedStartDate) {
      tableParams.startDate = formattedStartDate;
    }
    if (fromDate && toDate && fromDate.getTime() !== toDate.getTime()) {
      tableParams.endDate = formattedEndDate;
    }

    try {
      let baseUrls = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrls}/managerDashboard/getCustomDateLeakageTableData`, {
        params: tableParams,
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const exportData =
        response.data?.data && Array.isArray(response.data.data)
          ? response.data.data
          : response.data?.records && Array.isArray(response.data.records)
          ? response.data.records
          : [];

      if (!exportData.length) {
        alert('No data available to export');
        return;
      }

      const formattedData = exportData.map((record) => ({
        'Employee ID': record.employeeId || '',
        'Employee Name': record.employeeName || '',
        Date: record.date ? formatDate(record.date) : '',
        'Floor Hrs': record.totalLoggedHours || '',
        'Productive Hrs': record.totalProductiveHours || '',
        'Leakage Hrs': record.totalLeakageHours || '',
        'Effectiveness %': record.toolEffectiveness !== undefined ? Number(record.toolEffectiveness).toFixed(2) : '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'LeakageData');
      XLSX.writeFile(workbook, 'LeakageData.xlsx');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setDownloading(false); // hide loader
    }
  };

  return (
    <>
      <Card
        shadow="lg"
        radius="md"
        className="p-6 animate-fade-in border-l-8 border-[#19294F] shadow-2xl rounded-xl "
        style={{
          backgroundColor: '#F7F7F7',
          // Subtle hash pattern using an SVG data URI for a professional texture
          // backgroundImage:
          //   "url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2220%22 height=%2220%22 viewBox=%220 0 20 20%22%3E%3Crect width=%2220%22 height=%2220%22 fill=%22%23F7F7F7%22/%3E%3Cpath d=%22M0 0L20 20M20 0L0 20%22 stroke=%22%23E0E0E0%22 stroke-width=%220.5%22/%3E%3C/svg%3E')",
          // backgroundSize: '20px 20px',
        }}
      >
        <h2 className="text-xl font-bold mb-4 transition-all duration-500" style={{ color: '#19294F' }}>
          📊 Leakage Performance Table
        </h2>

        {/* Inline Filter Section */}
        <div className="flex flex-wrap justify-end items-center gap-4 mb-8">
          <div className="w-48">
            <Select
              options={clients}
              isLoading={loadingClients}
              placeholder="Select Client..."
              value={selectedClient}
              onChange={(selected) => {
                setSelectedClient(selected);
                setSelectedScope(null);
                fetchScopes(selected.value);
              }}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              components={animatedComponents}
            />
          </div>
          <div className="w-48">
            <Select
              options={scopes}
              isLoading={loadingScopes}
              placeholder="Select Scope..."
              value={selectedScope}
              isDisabled={!selectedClient}
              onChange={(selected) => setSelectedScope(selected)}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              components={animatedComponents}
            />
          </div>

          {/* Date Filters */}
          <div className="flex gap-2">
            {/* <DatePicker
              selected={fromDate}
              onChange={(date) => setFromDate(date)}
              placeholderText="From Date"
              className="w-36 p-2 border border-[#707070] rounded-lg transition-all duration-300 ease-in-out focus:border-[#19294F]"
              popperPlacement="bottom-start"
              popperContainer={({ children }) => <div className="relative z-[9999]">{children}</div>}
            />
            <DatePicker
              selected={toDate}
              onChange={(date) => setToDate(date)}
              placeholderText="To Date"
              className="w-36 p-2 border border-[#707070] rounded-lg transition-all duration-300 ease-in-out focus:border-[#19294F]"
              popperPlacement="bottom-start"
              popperContainer={({ children }) => <div className="relative z-[9999]">{children}</div>}
            /> */}
            <div className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-3 py-2 shadow-sm">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-[#19294F]" />
              <Flatpickr
                options={{
                  mode: 'range',
                  dateFormat: 'M j, Y',
                  animate: true,
                  onClose: (selectedDates) => {
                    if (selectedDates.length === 1) {
                      setFromDate(selectedDates[0]);
                      setToDate(new Date());
                    } else if (selectedDates.length === 2) {
                      setFromDate(selectedDates[0]);
                      setToDate(selectedDates[1]);
                    } else {
                      setFromDate(null);
                      setToDate(null);
                    }
                  },
                  onOpen: (selectedDates, dateStr, instance) => {
                    instance.jumpToDate(new Date());
                  },
                }}
                value={[]}
                placeholder="Select date range"
                className="w-64 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#19294F]"
              />
              <button
                onClick={() => {
                  setFromDate(null);
                  setToDate(null);
                  document.querySelector('.flatpickr-input')._flatpickr.clear(); // clear Flatpickr visual value
                }}
                className="text-blue-600 hover:text-blue-800 transition"
                title="Clear date range"
              >
                <IconReload className="w-5 h-5" />
              </button>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="px-4 py-2 rounded-full shadow-md hover:scale-105 transition-transform duration-200 text-[#19294F] border-[#19294F]"
            onClick={handleClearFilters}
          >
            Clear
          </Button>
        </div>

        {/* Search and Export Section */}
        <div className="flex items-center justify-between bg-[#F7F7F7] p-2 rounded-lg shadow-sm mb-4 transition-all duration-300">
          <div className="flex items-center flex-grow">
            <IconSearch size={20} className="text-[#19294F] mr-2" />
            <TextInput
              placeholder="Search Employee, ID, Scope..."
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
                setPage(1);
              }}
              className="w-full max-w-xs"
              variant="unstyled"
              style={{ color: '#19294F' }}
            />
          </div>
          <Button
            variant="outline"
            size="s"
            className="ml-4 text-[#19294F] border-[#19294F]"
            onClick={exportToExcel}
            disabled={downloading}
          >
            {downloading ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-blue-500 border-solid" />
                Downloading...
              </>
            ) : (
              'Export Excel'
            )}
          </Button>
        </div>
        {loadingTable ? (
          <div className="flex justify-center items-center mt-20">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-blue-500 border-solid"></div>
          </div>
        ) : (
          <>
            <div className="datatables">
              <DataTable
                striped
                className="shadow-lg transition-all duration-300 hover:shadow-2xl border border-gray-200"
                columns={[
                  {
                    accessor: 'employeeId',
                    title: <span style={{ color: '#f0f1f4', fontWeight: '600' }}>🔢 Employee ID</span>,
                    sortable: true,
                  },
                  {
                    accessor: 'employeeName',
                    title: <span style={{ color: '#f0f1f4', fontWeight: '600' }}>👤 Employee Name</span>,
                    sortable: true,
                  },
                  {
                    accessor: 'date',
                    title: <span style={{ color: '#f0f1f4', fontWeight: '600' }}>Date</span>,
                    sortable: true,
                    render: (record) => {
                      const d = new Date(record.date);
                      const month = (d.getMonth() + 1).toString().padStart(2, '0');
                      const day = d.getDate().toString().padStart(2, '0');
                      const year = d.getFullYear();
                      return `${month}-${day}-${year}`;
                    },
                  },
                  // {
                  //   accessor: 'norms',
                  //   title: <span style={{ color: '#f0f1f4', fontWeight: '600' }}>🎯 Norms</span>,
                  //   sortable: true,
                  // },
                  {
                    accessor: 'totalLoggedHours',
                    title: <span style={{ color: '#f0f1f4', fontWeight: '600' }}>⌛ Floor Hrs</span>,
                    sortable: true,
                  },
                  {
                    accessor: 'totalProductiveHours',
                    title: <span style={{ color: '#f0f1f4', fontWeight: '600' }}>⏱ Productive Hrs</span>,
                    sortable: true,
                  },
                  {
                    accessor: 'toolEffectiveness',
                    title: (
                      <div className="flex items-center">
                        <IconStar size={16} className="text-[#E5252E]" />
                        <span className="ml-1" style={{ color: '#f0f1f4', fontWeight: '600' }}>
                          Effectiveness %
                        </span>
                      </div>
                    ),
                    sortable: true,
                    render: (record) =>
                      record.toolEffectiveness !== undefined && record.toolEffectiveness !== null
                        ? Number(record.toolEffectiveness).toFixed(2)
                        : '',
                  },
                  {
                    accessor: 'totalLeakageHours',
                    title: (
                      <div className="flex items-center">
                        <IconHourglass size={16} className="text-[#E5252E]" />
                        <span className="ml-1" style={{ color: '#f0f1f4', fontWeight: '600' }}>
                          Leakage Hrs
                        </span>
                      </div>
                    ),
                    sortable: true,
                  },
                ]}
                records={displayedRecords}
                totalRecords={
                  tableData.filter((item) => Object.values(item).join(' ').toLowerCase().includes(search.toLowerCase())).length
                }
                page={page}
                onPageChange={setPage}
                recordsPerPage={PAGE_SIZE}
                paginationText={({ from, to, totalRecords }) => `Showing ${from} - ${to} of ${totalRecords}`}
              />
            </div>
          </>
        )}
        {/* <div className="flex justify-center mt-4">
          <Pagination total={Math.ceil(totalCount / PAGE_SIZE)} page={page} onChange={setPage} />
        </div> */}
      </Card>
    </>
  );
};

export default CardCompTableLeakage;
